"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { INSURANCE_CARD_CACHE_KEY } from "@/src/lib/data-fetchers/insurance-card-fetchers";
import {
  ORDER_PPN_RECOMMENDATIONS_CACHE_KEY,
  ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY,
} from "@/src/lib/data-fetchers/order-query-keys";
import {
  getEligibilityCheckByOrderId,
  getEligibilityCheckByReferralId,
  getEligibilityCheckByTaskId,
  recheckOrderEligibility,
  recheckTaskEligibility,
  verifyInsuranceEligibilityAtIntake,
  type VerifyInsuranceEligibilityRequestPayload,
} from "@/src/requests/stedi-eligibility-check";

export const STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "stedi-eligibility-check";
export const INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY = "intake-stedi-eligibility-check";

export function useStediEligibilityCheck(referralId: string | null) {
  const query = useQuery({
    queryKey: [STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId],
    queryFn: () => {
      if (!referralId) return null;

      return getEligibilityCheckByReferralId(referralId);
    },
    enabled: !!referralId,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useRecheckReferralEligibility(taskId: string, referralId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => recheckTaskEligibility(taskId),
    onSuccess: (data) => {
      if (data.check) {
        queryClient.setQueryData([STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId], data.check);

        return;
      }

      void queryClient.invalidateQueries({ queryKey: [STEDI_ELIGIBILITY_CHECK_CACHE_KEY, referralId] });
    },
  });
}

export function useIntakeStediEligibilityCheck(taskId: string | null) {
  const query = useQuery({
    queryKey: [INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId],
    queryFn: () => {
      if (!taskId) return null;

      return getEligibilityCheckByTaskId(taskId);
    },
    enabled: !!taskId,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useVerifyInsuranceEligibilityAtIntake(taskId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: VerifyInsuranceEligibilityRequestPayload) => {
      if (!taskId) {
        throw new Error("Task id is required to verify insurance eligibility");
      }

      return verifyInsuranceEligibilityAtIntake(taskId, payload);
    },
    onSuccess: (data) => {
      if (!taskId) return;

      queryClient.setQueryData([INTAKE_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, taskId], data.check);
    },
  });
}

export function useOrderStediEligibilityCheck(orderId: string | null) {
  const query = useQuery({
    queryKey: [ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId],
    queryFn: () => {
      if (!orderId) return null;

      return getEligibilityCheckByOrderId(orderId);
    },
    enabled: !!orderId,
  });

  return {
    eligibilityCheck: query.data ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useRecheckOrderEligibility(orderId: string | null, _patientId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!orderId) {
        throw new Error("Order id is required to re-check insurance eligibility");
      }

      return recheckOrderEligibility(orderId, true);
    },
    onSuccess: (data) => {
      if (!orderId) return;

      void queryClient.invalidateQueries({ queryKey: [ORDER_PPN_RECOMMENDATIONS_CACHE_KEY, orderId] });
      void queryClient.invalidateQueries({ queryKey: [INSURANCE_CARD_CACHE_KEY] });

      if (data.check) {
        queryClient.setQueryData([ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId], data.check);

        return;
      }

      void queryClient.invalidateQueries({ queryKey: [ORDER_STEDI_ELIGIBILITY_CHECK_CACHE_KEY, orderId] });
    },
  });
}
